import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, MapPin, Phone, Clock, MessageCircle, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useSite } from "@/context/SiteContext";
import { api, errMsg, telLink, waLink } from "@/lib/api";
import { Reveal, SectionTitle } from "@/components/site/Primitives";
import { getConsent } from "@/components/site/CookieBanner";

const TIMES = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { name: "", phone: "", email: "", date: today(), time: "20:00", guests: 2, note: "", kvkk_accepted: false, marketing_consent: false };

function ReservationForm() {
  const { settings } = useSite();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.kvkk_accepted) return toast.error("Lütfen KVKK Aydınlatma Metni'ni okuduğunuzu onaylayın.");
    setLoading(true);
    try {
      await api.post("/reservations", { ...form, guests: Number(form.guests) });
      setDone(true);
      setForm(EMPTY);
      toast.success("Rezervasyon talebiniz alındı! En kısa sürede sizi arayarak teyit edeceğiz.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (settings.reservations_open === false) {
    return (
      <div className="rounded-lg border border-[#D4AF37]/25 bg-[#1F1411] p-8" data-testid="reservation-closed">
        <p className="font-serif-ym text-2xl">Online rezervasyon şu an kapalı.</p>
        <a href={telLink(settings.phone)} className="btn-gold mt-6">Hemen Ara</a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-lg border border-[#D4AF37]/25 bg-[#1F1411] p-6 sm:p-10 space-y-5" data-testid="reservation-form">
      {done && (
        <div className="rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 text-sm text-[#E5C158]" data-testid="reservation-success">
          Teşekkürler! Talebiniz bize ulaştı. Rezervasyonunuz telefonla teyit edildikten sonra kesinleşir.
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <input required minLength={2} className="ym-input" placeholder="Ad Soyad *" value={form.name} onChange={set("name")} data-testid="reservation-name-input" />
        <input required minLength={7} type="tel" className="ym-input" placeholder="Telefon *" value={form.phone} onChange={set("phone")} data-testid="reservation-phone-input" />
      </div>
      <input type="email" className="ym-input" placeholder="E-posta (isteğe bağlı)" value={form.email} onChange={set("email")} data-testid="reservation-email-input" />
      <div className="grid grid-cols-3 gap-4">
        <input required type="date" min={today()} className="ym-input [color-scheme:dark]" value={form.date} onChange={set("date")} data-testid="reservation-date-input" />
        <select className="ym-input" value={form.time} onChange={set("time")} data-testid="reservation-time-select">
          {TIMES.map((t) => <option key={t}>{t}</option>)}
        </select>
        <select className="ym-input" value={form.guests} onChange={set("guests")} data-testid="reservation-guests-select">
          {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n} kişi</option>)}
        </select>
      </div>
      <textarea rows={3} maxLength={500} className="ym-input resize-none" placeholder="Notunuz (doğum günü, bebek sandalyesi vb.)" value={form.note} onChange={set("note")} data-testid="reservation-note-input" />
      <label className="flex items-start gap-3 text-xs text-[#CBBBAE] leading-relaxed cursor-pointer">
        <Checkbox checked={form.kvkk_accepted} onCheckedChange={(v) => set("kvkk_accepted")(!!v)} className="mt-0.5 border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#140D0B]" data-testid="reservation-kvkk-checkbox" />
        <span>
          <Link to="/yasal/kvkk-aydinlatma-metni" target="_blank" className="text-[#D4AF37] underline" data-testid="reservation-kvkk-link">KVKK Aydınlatma Metni</Link>'ni
          okudum; kişisel verilerimin rezervasyon talebimin yönetilmesi amacıyla işleneceği konusunda bilgilendirildim. *
        </span>
      </label>
      <label className="flex items-start gap-3 text-xs text-[#CBBBAE] leading-relaxed cursor-pointer">
        <Checkbox checked={form.marketing_consent} onCheckedChange={(v) => set("marketing_consent")(!!v)} className="mt-0.5 border-[#D4AF37]/60 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#140D0B]" data-testid="reservation-marketing-checkbox" />
        <span>
          Kampanya ve duyurulardan haberdar olmak için <Link to="/yasal/acik-riza-metni" target="_blank" className="text-[#D4AF37] underline">Açık Rıza ve Ticari Elektronik İleti</Link> metni kapsamında
          tarafıma ileti gönderilmesine onay veriyorum. (İsteğe bağlı)
        </span>
      </label>
      <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60" data-testid="reservation-form-submit-btn">
        {loading ? <Loader2 className="animate-spin" size={16} /> : <CalendarCheck size={16} />} Rezervasyon Talebi Gönder
      </button>
    </form>
  );
}

function ContactForm() {
  const [f, setF] = useState({ name: "", phone: "", email: "", message: "", kvkk_accepted: false });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.kvkk_accepted) return toast.error("Lütfen KVKK Aydınlatma Metni'ni okuduğunuzu onaylayın.");
    setLoading(true);
    try {
      await api.post("/messages", f);
      toast.success("Mesajınız iletildi, teşekkürler!");
      setF({ name: "", phone: "", email: "", message: "", kvkk_accepted: false });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="mt-6 rounded-lg border border-[#D4AF37]/15 bg-[#1F1411]/60 p-6 sm:p-8 space-y-4" data-testid="contact-form">
      <p className="font-serif-ym text-2xl">Bize yazın</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <input required minLength={2} className="ym-input" placeholder="Ad Soyad *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" />
        <input className="ym-input" placeholder="Telefon veya e-posta" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone-input" />
      </div>
      <textarea required minLength={3} rows={3} className="ym-input resize-none" placeholder="Mesajınız, öneriniz veya toplu sipariş talebiniz *" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" />
      <label className="flex items-start gap-3 text-xs text-[#CBBBAE] cursor-pointer">
        <Checkbox checked={f.kvkk_accepted} onCheckedChange={(v) => setF({ ...f, kvkk_accepted: !!v })} className="mt-0.5 border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-[#140D0B]" data-testid="contact-kvkk-checkbox" />
        <span><Link to="/yasal/kvkk-aydinlatma-metni" target="_blank" className="text-[#D4AF37] underline">KVKK Aydınlatma Metni</Link>'ni okudum ve bilgilendirildim. *</span>
      </label>
      <button type="submit" disabled={loading} className="btn-ghost-gold w-full" data-testid="contact-submit-btn">{loading ? <Loader2 className="animate-spin" size={16} /> : <MessageCircle size={16} />} Mesaj Gönder</button>
    </form>
  );
}

function MapEmbed({ query }) {
  const [allowed, setAllowed] = useState(!!getConsent()?.functional);
  useEffect(() => {
    const h = () => setAllowed(!!getConsent()?.functional);
    window.addEventListener("cookie-consent-changed", h);
    return () => window.removeEventListener("cookie-consent-changed", h);
  }, []);
  if (!allowed) {
    return (
      <div className="h-64 rounded-lg border border-[#D4AF37]/20 bg-[#1F1411] grid place-items-center text-center p-6" data-testid="map-consent-placeholder">
        <div>
          <MapPin className="mx-auto text-[#D4AF37]" />
          <p className="mt-3 text-sm text-[#CBBBAE]">Harita Google tarafından sağlanır ve çerez kullanabilir.</p>
          <button onClick={() => setAllowed(true)} className="btn-ghost-gold mt-4 !py-2 !text-xs" data-testid="map-load-btn">Haritayı Göster</button>
        </div>
      </div>
    );
  }
  return (
    <iframe
      title="Yeni Mercan Restoran konum"
      className="h-64 w-full rounded-lg border border-[#D4AF37]/20 grayscale-[40%]"
      loading="lazy"
      src={`https://www.google.com/maps?q=${encodeURIComponent(query)}&output=embed`}
      data-testid="map-iframe"
    />
  );
}

export function ReservationContact() {
  const { settings } = useSite();
  const rows = [
    { icon: MapPin, label: "Adres", value: settings.address },
    { icon: Phone, label: "Telefon", value: settings.phone, href: telLink(settings.phone) },
    { icon: Clock, label: "Çalışma Saatleri", value: settings.hours },
  ];
  return (
    <section id="rezervasyon" className="ym-dark py-24 sm:py-32 relative grain" data-testid="reservation-section">
      <div id="iletisim" className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-14 relative">
        <Reveal className="lg:col-span-5">
          <SectionTitle eyebrow="Rezervasyon & İletişim" title={<>Masanız <span className="gold-text italic">hazır</span> olsun.</>} />
          <p className="mt-6 text-[#CBBBAE] leading-relaxed">Kalabalık sofralar, aile yemekleri ya da gece yarısı çorbası... Formu doldurun, sizi arayıp teyit edelim.</p>
          <ul className="mt-10 space-y-6">
            {rows.map(({ icon: Icon, label, value, href }) => (
              <li key={label} className="flex gap-4">
                <div className="h-11 w-11 shrink-0 rounded-full border border-[#D4AF37]/40 grid place-items-center text-[#D4AF37]"><Icon size={18} /></div>
                <div>
                  <p className="eyebrow !text-[10px]">{label}</p>
                  {href ? <a href={href} className="mt-1 block text-lg hover:text-[#D4AF37]" data-testid="contact-phone-link">{value}</a> : <p className="mt-1 text-lg">{value}</p>}
                </div>
              </li>
            ))}
          </ul>
          <a href={waLink(settings.whatsapp, "Merhaba, rezervasyon yapmak istiyorum.")} target="_blank" rel="noopener noreferrer" className="btn-ghost-gold mt-10" data-testid="contact-whatsapp-btn">
            <MessageCircle size={16} /> WhatsApp'tan yazın
          </a>
          <div className="mt-10"><MapEmbed query={settings.map_query || settings.address} /></div>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.15}>
          <ReservationForm />
          <ContactForm />
        </Reveal>
      </div>
    </section>
  );
}
